import { Card, DatePicker } from 'antd';
import styled from 'styled-components';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Pie } from '@ant-design/plots';
import { supervisionsPieChartData } from '../../../utils/setDataChart/supervisionsPieChart';
import {
  SUPERVISIONS_STATUS_COLORS,
  supervisionsPieChartConfig
} from '../../../utils/setChartConfig/supervisionsPieChartConfig';

const StyledContent = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;

  .donut {
    position: relative;
    flex: 1 1 200px;
    min-width: 200px;
  }

  .donut-caption {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    margin-top: 14px;
    text-align: center;
    font-size: 12px;
    color: var(--dimGray);
    pointer-events: none;
  }
`;

const StyledLegend = styled.ul`
  flex: 1 1 180px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }

  .label {
    flex: 1;
  }

  .value {
    font-weight: 700;
  }
`;

/**
 * SupervisionsPieChart component displays a donut chart representing the status of supervisions using Ant Design components and @ant-design/plots.
 * It utilizes custom hooks for data fetching and chart configuration.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Function} props.t - Translation function for internationalization.
 * @param {Object} props.supervisionsStatus - Object containing the counts for different supervision statuses.
 * @param {Function} props.setStatusDate - Function to set the date of the status.
 * @returns {JSX.Element} SupervisionsPieChart component.
 */
export const SupervisionsPieChart = ({
  t,
  supervisionsStatus,
  setStatusDate
}) => {
  const { request, waiting, published, waiting_booking, booked, in_progress } =
    supervisionsStatus || {};

  const data = supervisionsPieChartData({
    request,
    waiting,
    published,
    waiting_booking,
    booked,
    in_progress,
    t
  });
  const total = data.reduce((acc, { value }) => acc + value, 0);

  return (
    <div>
      <Card
        title={t('dashboard.tabs.supervisions.status')}
        extra={
          <DatePicker
            onChange={(date) => setStatusDate(date)}
            format="DD/MM/YYYY"
          />
        }
        className="dashboard-card"
      >
        <StyledContent>
          <div className="donut">
            <Pie {...supervisionsPieChartConfig(data, `${total}`)} />
            <span className="donut-caption">
              {t('dashboard.tabs.supervisions.supervisions')}
            </span>
          </div>
          <StyledLegend>
            {data.map(({ status, value }, index) => (
              <li key={status}>
                <span
                  className="dot"
                  style={{ background: SUPERVISIONS_STATUS_COLORS[index] }}
                />
                <span className="label">{status}</span>
                <span className="value">{value}</span>
              </li>
            ))}
          </StyledLegend>
        </StyledContent>
      </Card>
    </div>
  );
};

SupervisionsPieChart.propTypes = {
  t: PropTypes.func.isRequired,
  supervisionsStatus: PropTypes.shape({}).isRequired,
  setStatusDate: PropTypes.func.isRequired
};
